import { CustomFieldIcon } from "@jugl-web/domain-resources/custom-fields/components/CustomFieldIcon";
import { TaskFieldSettingsMap } from "@jugl-web/rest-api/tasks/models/TaskBoard/TaskBoardDto";
import {
  SpecificOrAnyString,
  reorder,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import { MY_TASKS_FIELD_SETTINGS_MAP_KEY } from "@jugl-web/utils/storage";
import { partition } from "lodash";
import { useCallback, useMemo } from "react";
import { TaskDefaultFieldId, TaskFieldId } from "../types";
import { useTaskBoardsQuery } from "./useTaskBoardsQuery";
import { useTaskCustomFields } from "./useTaskCustomFields";
import { TaskDefaultField, useTaskDefaultFields } from "./useTaskDefaultFields";
import { useTaskLabels } from "./useTaskLabels";
import { useUpdateTaskBoard } from "./useUpdateTaskBoard";

export type VisibleDefaultFieldsVariant = "generic" | "table-view-specific";

interface UseTaskFieldListOptions {
  entityId: string;
  boardId: string | null;
  /**
   * Based on the screen, the default fields that are visible are different.
   * For example, there are fields that are specific to the table view only and should not be visible in the task details.
   */
  visibleDefaultFieldsVariant?: VisibleDefaultFieldsVariant;
}

export interface TaskField<TId extends string>
  extends Omit<TaskDefaultField<TId>, "type"> {
  order: number;
  visibility: boolean;
  isDefault: boolean;
  createdBy: string | null;
}

export type DraggableTaskField<TId extends string> = TaskField<TId> & {
  draggableIndex: number;
};

export const useTaskFieldList = ({
  entityId,
  boardId,
  visibleDefaultFieldsVariant = "table-view-specific",
}: UseTaskFieldListOptions) => {
  const isInBoardContext = boardId !== null;

  const [myTasksFieldSettingsMap, setMyTasksFieldSettingsMap] =
    useLocalStorage<TaskFieldSettingsMap>(MY_TASKS_FIELD_SETTINGS_MAP_KEY, {});

  const { areLabelsSupported } = useTaskLabels({ entityId });

  const { getBoardByIdFromCache } = useTaskBoardsQuery({ entityId });
  const board = boardId ? getBoardByIdFromCache(boardId) : undefined;

  const { getDefaultFields } = useTaskDefaultFields({
    entityId,
    boardId,
  });

  const { t } = useTranslations();
  const { toast } = useToast();

  const fieldSettingsMap = useMemo<TaskFieldSettingsMap>(() => {
    if (!isInBoardContext) {
      return myTasksFieldSettingsMap;
    }

    return board?.fieldsSettings || {};
  }, [board, isInBoardContext, myTasksFieldSettingsMap]);

  const isFieldEnabled = useCallback(
    (fieldId: TaskFieldId): boolean =>
      fieldId in fieldSettingsMap ? fieldSettingsMap[fieldId].visibility : true,
    [fieldSettingsMap]
  );

  const extractFieldOrder = useCallback(
    (fieldId: TaskFieldId): number =>
      fieldId in fieldSettingsMap ? fieldSettingsMap[fieldId].order : 999,
    [fieldSettingsMap]
  );

  const { boardCustomFields } = useTaskCustomFields({ entityId, boardId });

  const defaultFields = useMemo<TaskField<TaskDefaultFieldId>[]>(
    () =>
      getDefaultFields()
        // We don't consider task name as a default field, as it is static.
        // If needed it should be included in the list manually
        .filter((field) => field.id !== "task_name")
        .map((field) => ({
          ...field,
          visibility: isFieldEnabled(field.id),
          order: extractFieldOrder(field.id),
          isDefault: true,
          createdBy: null,
        })),
    [getDefaultFields, extractFieldOrder, isFieldEnabled]
  );

  const customFields = useMemo<TaskField<string>[]>(() => {
    if (!isInBoardContext) {
      return [];
    }

    return boardCustomFields.map((field) => ({
      id: field.id,
      title: field.name,
      icon: <CustomFieldIcon type={field.type} />,
      visibility: isFieldEnabled(field.id),
      order: extractFieldOrder(field.id),
      isDefault: false,
      createdBy: field.createdBy,
    }));
  }, [boardCustomFields, extractFieldOrder, isFieldEnabled, isInBoardContext]);

  const allFieldsSortedIndexed = useMemo<DraggableTaskField<TaskFieldId>[]>(
    () =>
      [...defaultFields, ...customFields]
        .sort((a, b) => a.order - b.order)
        .map((field, index) => ({ ...field, draggableIndex: index })),
    [defaultFields, customFields]
  );

  const fieldsMap = useMemo<Map<TaskFieldId, DraggableTaskField<TaskFieldId>>>(
    () => new Map(allFieldsSortedIndexed.map((field) => [field.id, field])),
    [allFieldsSortedIndexed]
  );

  const filteredFields = useMemo<DraggableTaskField<TaskFieldId>[]>(() => {
    let initialFields = [...allFieldsSortedIndexed];

    if (!areLabelsSupported) {
      initialFields = initialFields.filter((field) => field.id !== "label");
    }

    if (visibleDefaultFieldsVariant === "table-view-specific") {
      return initialFields;
    }

    const tableViewSpecificFields: string[] = [
      "checklist",
      "activity",
      "created_at",
      "updated_at",
    ] satisfies TaskDefaultFieldId[];

    return initialFields.filter((f) => !tableViewSpecificFields.includes(f.id));
  }, [allFieldsSortedIndexed, areLabelsSupported, visibleDefaultFieldsVariant]);

  const [
    enabledFields,
    remainingFields,
    remainingDefaultFields,
    remainingCustomFields,
  ] = useMemo(() => {
    const [enabled, remaining] = partition(filteredFields, (f) => f.visibility);

    const [remainingDefault, remainingCustom] = partition(
      remaining,
      (f) => f.isDefault
    );

    return [enabled, remaining, remainingDefault, remainingCustom];
  }, [filteredFields]);

  const getFieldById = useCallback(
    (fieldId: TaskFieldId) => fieldsMap.get(fieldId),
    [fieldsMap]
  );

  const { updateBoard } = useUpdateTaskBoard({ entityId });

  const showFieldVisibilityChangeToast = useCallback(
    (fieldId: TaskFieldId) => {
      const matchingField = getFieldById(fieldId);

      if (!matchingField) {
        return;
      }

      toast(
        <span>
          {matchingField.visibility
            ? t(
                {
                  id: "feedback.board-field-disabled",
                  defaultMessage: "Field {name} has been disabled in the Board",
                },
                {
                  name: (
                    <span className="font-bold">{matchingField.title}</span>
                  ),
                }
              )
            : t(
                {
                  id: "feedback.board-field-enabled",
                  defaultMessage: "Field {name} has been enabled in the Board",
                },
                {
                  name: (
                    <span className="font-bold">{matchingField.title}</span>
                  ),
                }
              )}
        </span>
      );
    },
    [getFieldById, t, toast]
  );

  const toggleFieldVisibility = useCallback(
    (fieldId: SpecificOrAnyString<TaskDefaultFieldId>) => {
      const updatedFieldSettingsEntry = {
        [fieldId]: {
          visibility: !isFieldEnabled(fieldId),
          order: extractFieldOrder(fieldId),
        },
      };

      if (!isInBoardContext) {
        setMyTasksFieldSettingsMap((prev) => ({
          ...prev,
          ...updatedFieldSettingsEntry,
        }));

        return;
      }

      if (!board) {
        return;
      }

      updateBoard(
        board.id,
        {
          settings: {
            fields: {
              ...fieldSettingsMap,
              ...updatedFieldSettingsEntry,
            },
          },
        },
        true
      );

      showFieldVisibilityChangeToast(fieldId);
    },
    [
      isFieldEnabled,
      extractFieldOrder,
      isInBoardContext,
      board,
      updateBoard,
      fieldSettingsMap,
      showFieldVisibilityChangeToast,
      setMyTasksFieldSettingsMap,
    ]
  );

  const reorderEnabledFields = useCallback(
    (sourceDraggableIndex: number, destinationDraggableIndex: number) => {
      const reorderedFields = reorder(
        allFieldsSortedIndexed,
        sourceDraggableIndex,
        destinationDraggableIndex
      );

      const updatedFieldSettingsMap =
        reorderedFields.reduce<TaskFieldSettingsMap>((acc, field, index) => {
          acc[field.id] = {
            visibility: field.visibility,
            order: index + 1,
          };
          return acc;
        }, {});

      if (!isInBoardContext) {
        setMyTasksFieldSettingsMap(updatedFieldSettingsMap);
        return;
      }

      if (!board) {
        return;
      }

      updateBoard(
        board.id,
        { settings: { fields: updatedFieldSettingsMap } },
        true
      );
    },
    [
      allFieldsSortedIndexed,
      board,
      isInBoardContext,
      setMyTasksFieldSettingsMap,
      updateBoard,
    ]
  );

  return {
    allFieldsSortedIndexed,
    enabledFields,
    remainingFields,
    remainingDefaultFields,
    remainingCustomFields,
    getFieldById,
    isFieldEnabled,
    extractFieldOrder,
    toggleFieldVisibility,
    reorderEnabledFields,
  };
};
